/**
 * Case Document Import API client.
 *
 * Replaces the legacy `extraction-scope` API. Represents the "Import to case"
 * action: copy a lake document into a case with an optional page-scope
 * selection.
 */

import { apiClient } from './client'

export type CaseDocumentImportScopeType = 'all' | 'pages' | 'tabular'

export interface CaseDocumentImport {
  id: string
  case_document_id: string
  lake_document_id: string
  case_id: string
  scope_type: CaseDocumentImportScopeType
  selected_pages: number[] | null
  /** Column names kept; non-null only when scope_type='tabular'. */
  selected_columns: string[] | null
  /** 0-based `__row_index` values kept; non-null only when scope_type='tabular'. */
  selected_rows: number[] | null
  scope_note: string | null
  created_at: string
  updated_at: string
  created_by: string | null
  reviewed_at: string | null
  reviewed_by: string | null
}

export interface CaseDocumentImportWithCase extends CaseDocumentImport {
  case_name: string
}

export interface CreateImportRequest {
  case_id: string
  scope_type: CaseDocumentImportScopeType
  selected_pages?: number[] | null
  /** Required (non-empty) when scope_type='tabular'. */
  selected_columns?: string[] | null
  /** Required (non-empty) when scope_type='tabular'. */
  selected_rows?: number[] | null
  scope_note?: string | null
}

export const caseDocumentImportApi = {
  /**
   * Create or update an import for the (lake_document, case) pair. POST is
   * idempotent — re-importing the same pair updates the scope in place.
   */
  async upsert(lakeDocumentId: string, request: CreateImportRequest): Promise<CaseDocumentImport> {
    return apiClient.post<CaseDocumentImport>(
      `/v1/lake-documents/${lakeDocumentId}/imports`,
      request
    )
  },

  /**
   * List all imports for a lake document (one row per case that imported it).
   */
  async list(lakeDocumentId: string): Promise<CaseDocumentImportWithCase[]> {
    return apiClient.get<CaseDocumentImportWithCase[]>(
      `/v1/lake-documents/${lakeDocumentId}/imports`
    )
  },

  /**
   * Find the import for a specific (lake_document, case) pair, or null.
   */
  async getByCase(
    lakeDocumentId: string,
    caseId: string
  ): Promise<CaseDocumentImportWithCase | null> {
    const imports = await caseDocumentImportApi.list(lakeDocumentId)
    return imports.find(i => i.case_id === caseId) ?? null
  },

  /**
   * Delete an import: removes the case_document, its S3 copy, and the import row.
   */
  async remove(lakeDocumentId: string, importId: string): Promise<void> {
    await apiClient.delete(`/v1/lake-documents/${lakeDocumentId}/imports/${importId}`)
  },

  /**
   * Mark an import as reviewed.
   */
  async markAsReviewed(lakeDocumentId: string, importId: string): Promise<CaseDocumentImport> {
    return apiClient.patch<CaseDocumentImport>(
      `/v1/lake-documents/${lakeDocumentId}/imports/${importId}/review`,
      {}
    )
  },
}
